import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import CustomPagination from "../../../utils/paginationUtils";
import editIcon from "../../../assets/edit-icon.svg";
import eyeIcon from "../../../assets/eye.svg";
import searchIcon from "../../../assets/search-new-icon.svg";
import downIcon from "../../../assets/down-icon.svg";
import CLOSE from "../../../assets/CLOSE.svg";
import closeIcon from "../../../assets/crossIcon.svg";
import Switch from "@mui/material/Switch";
import { Country, State } from "country-state-city";
import { ProcedureFlowServices } from "../../../services/ProcedureFlowServices";
import { ReportServices } from "../../../services/ReportServices";
import { HospitalServices } from "../../../services/HospitalServices";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";
import { FilterDropdown } from "./FilterDropdown";
import EditSchemeModal from "./EditSchemeModal";

const INDIAN_STATE_LIST = State.getStatesOfCountry("IN");
const INDIAN_STATES = INDIAN_STATE_LIST.map((s) => s.name);

/** Display label for a state id/code or name as stored on the scheme */
function formatStateLabelForDisplay(idOrName) {
  const raw = String(idOrName ?? "").trim();
  if (!raw) return "";
  if (INDIAN_STATES.includes(raw)) return raw;
  const byCode = INDIAN_STATE_LIST.find(
    (s) => s.isoCode === raw || s.name === raw
  );
  return byCode ? byCode.name : raw;
}

/** Comma-separated state codes (preferred) or stateId — for table State column */
function formatStateIdsForTable(stateIds) {
  if (!Array.isArray(stateIds) || stateIds.length === 0) return "—";
  const parts = stateIds
    .map((s) => {
      if (s != null && typeof s === "object") {
        const code = s.stateCode != null && String(s.stateCode).trim() !== "" ? String(s.stateCode).trim() : null;
        const id = s.stateId != null && String(s.stateId).trim() !== "" ? String(s.stateId).trim() : null;
        return code || id || "";
      }
      return String(s ?? "").trim();
    })
    .filter(Boolean);
  return parts.length ? parts.join(", ") : "—";
}

/** Aligns with Manage Scheme: stateIds[] entries may include status active/inactive. */
function isSchemeStateEntryActive(entry) {
  if (entry != null && typeof entry === "object" && "status" in entry) {
    return String(entry.status).trim().toLowerCase() !== "inactive";
  }
  return true;
}

/** Display names for scheme.stateIds — only active states (excludes inactive mappings). */
function getActiveStateNamesFromSchemeStateIds(stateIds) {
  if (!Array.isArray(stateIds) || stateIds.length === 0) return [];
  return stateIds
    .filter((s) => isSchemeStateEntryActive(s))
    .map((s) => (typeof s === "object" && s?.stateId != null ? String(s.stateId) : String(s)))
    .filter(Boolean);
}

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

/** For procedure search: case-insensitive, trim, collapse whitespace */
function normalizeProcedureNameSearch(s) {
  return String(s ?? "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

/** Add-mapping dropdown / chips: show ID first, then name */
function formatProcedureOptionLabel(p) {
  const id = String(p?.procedureId ?? "").trim();
  const name = String(p?.procedure ?? "").trim();
  if (id && name) return `${id} — ${name}`;
  return id || name || "—";
}

export default function ManageProcedureSchemeMapping() {
  const [panelVisible, setPanelVisible] = useState(false);
  const [panelType, setPanelType] = useState("add"); // "add" | "edit" | "view"
  const [currentMapping, setCurrentMapping] = useState({
    schemeId: "",
    schemeName: "",
    procedureId: "",
    procedureName: "",
  });
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mappingsLoading, setMappingsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [schemes, setSchemes] = useState([]);
  const [schemeFilter, setSchemeFilter] = useState("");
  const [procedureSearchInput, setProcedureSearchInput] = useState("");
  const [procedureSearchKey, setProcedureSearchKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [inactiveModalOpen, setInactiveModalOpen] = useState(false);
  const [rowForInactive, setRowForInactive] = useState(null);
  const [users, setUsers] = useState(null);
  const searchDebounceRef = useRef(null);
  const savingRef = useRef(false);
  const [allProceduresFromMaster, setAllProceduresFromMaster] = useState([]);
  const [panelProceduresLoading, setPanelProceduresLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [initialMappingForEdit, setInitialMappingForEdit] = useState(null);
  const [selectedProcedures, setSelectedProcedures] = useState([]); // multi-select for add mode
  const [selectedForUnmap, setSelectedForUnmap] = useState([]); // mapped procedures selected for unmapping
  const [alreadyMappedProcedureIds, setAlreadyMappedProcedureIds] = useState(new Set());
  const [selectedState, setSelectedState] = useState("");
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [selectedStates, setSelectedStates] = useState([]); // multi-select for STATE type
  const [selectedHospitals, setSelectedHospitals] = useState([]); // multi-select for HOSPITAL type
  const [selectedCountry, setSelectedCountry] = useState("IN");
  const [hospitalList, setHospitalList] = useState([]);
  const [hospitalDropdownValue, setHospitalDropdownValue] = useState("");
  const [editSchemeModalOpen, setEditSchemeModalOpen] = useState(false);
  const [schemeForEditModal, setSchemeForEditModal] = useState(null);

  const fetchSchemes = async () => {
    try {
      const limit = 500;
      const first = await ProcedureFlowServices.getDefaultSchemeTypes({
        page: 1,
        limit,
      });
      const apiData = first?.data;
      const totalPages = Math.max(1, apiData?.totalPages ?? 1);
      let list = Array.isArray(apiData?.data) ? apiData.data : [];

      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            ProcedureFlowServices.getDefaultSchemeTypes({
              page: i + 2,
              limit,
            })
          )
        );
        rest.forEach((res) => {
          const pageList = Array.isArray(res?.data?.data) ? res.data.data : [];
          list = list.concat(pageList);
        });
      }

      const mapped = list.map((item) => ({
        id: item.id,
        name: item.name,
        procedureCostMapping: item.procedureCostMapping ?? null,
        stateIds: item.stateIds ?? null,
        schemeCountryId: item.schemeCountryId ?? "IN",
        childProcedureMapping: item.childProcedureMapping ?? false,
        status: item.status,
        details: item.details,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        createdBy: item.createdBy,
        updatedBy: item.updatedBy,
      }));
      setSchemes(mapped);
      return mapped;
    } catch (err) {
      handleApiErrorResponses(err);
      setSchemes([]);
      return [];
    }
  };

  /** Fetch already-mapped procedureIds for a scheme (to exclude from add list) */
  const fetchAlreadyMappedProcedures = async (schemeId) => {
    if (!schemeId) {
      setAlreadyMappedProcedureIds(new Set());
      return;
    }
    try {
      const res = await ProcedureFlowServices.getSchemeProcedureList({
        schemeId,
        limit: 9999,
        page: 1,
      });
      const list = res?.data?.list ?? [];
      const ids = new Set(
        list
          .filter((item) => item.status === "ACTIVE")
          .map((item) => String(item.procedureId ?? ""))
          .filter(Boolean)
      );
      setAlreadyMappedProcedureIds(ids);
    } catch (err) {
      setAlreadyMappedProcedureIds(new Set());
    }
  };

  /** Fetch complete procedure list from default-procedure-list-v2 (master list, no scheme filter) */
  const fetchAllProceduresFromMaster = async () => {
    setPanelProceduresLoading(true);
    try {
      const limit = 500;
      let allList = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const res = await ProcedureFlowServices.getDefaultProcedureListV2({
          page,
          limit,
        });
        const list = Array.isArray(res?.data?.list) ? res.data.list : [];
        allList = allList.concat(list);
        hasMore = res?.data?.hasMore === true && list.length >= limit;
        page += 1;
      }
      const seen = new Set();
      const deduped = allList.filter((item) => {
        const pid = item.procedureId;
        if (!pid || seen.has(pid)) return false;
        seen.add(pid);
        return true;
      });
      setAllProceduresFromMaster(
        deduped.map((item) => ({
          procedureId: item.procedureId,
          procedure: (item.procedure ?? "").trim(),
        })).filter((p) => p.procedureId && p.procedure)
      );
    } catch (err) {
      console.error("Failed to fetch procedure list", err);
      setAllProceduresFromMaster([]);
    } finally {
      setPanelProceduresLoading(false);
    }
  };

  const getUsersDetails = async () => {
    try {
      const response = await ReportServices.getUsers();
      setUsers(response?.data?.users ?? null);
    } catch (err) {
      handleApiErrorResponses(err);
    }
  };

  const [baseGroupedMappings, setBaseGroupedMappings] = useState([]);
  const [allGroupedMappings, setAllGroupedMappings] = useState([]);

  const fetchMappings = async () => {
    if (schemes.length === 0) return;
    setMappingsLoading(true);
    try {
      // Fetch all data (paginate through API to get full list) so we can group correctly
      let allList = [];
      let currentPage = 1;
      let hasMore = true;
      const fetchLimit = 100;

      while (hasMore) {
        const res = await ProcedureFlowServices.getSchemeProcedureList({
          page: currentPage,
          limit: fetchLimit,
          ...(schemeFilter ? { schemeId: schemeFilter } : {}),
        });
        const list = res?.data?.list ?? [];
        allList = allList.concat(list);
        hasMore = list.length >= fetchLimit;
        currentPage += 1;
      }

      const rawMapped = allList.map((item) => {
        const schemeName =
          schemes.find((s) => String(s.id) === String(item.schemeId))?.name ?? "";
        return {
          id: item.id,
          schemeId: item.schemeId,
          schemeName,
          procedureId: item.procedureId,
          procedureName: item.proceduremaster?.procedure ?? "",
          status: item.status,
          mappedBy: item.createdBy,
          mappedOn: item.createdAt,
        };
      });

      // Fetch cost data for unique schemes to get state/hospital per procedure
      const uniqueSchemeIds = [...new Set(rawMapped.map((m) => m.schemeId).filter(Boolean))];
      const costMap = {}; // (schemeId, procedureId) -> [displayName, ...]

      await Promise.all(
        uniqueSchemeIds.map(async (schemeId) => {
          const scheme = schemes.find((s) => String(s.id) === String(schemeId));
          const costType = String(scheme?.procedureCostMapping ?? "").toUpperCase().trim();
          try {
            const costRes = await ProcedureFlowServices.getSchemeProcedureCostList({
              schemeId,
              procedureCostMapping: costType || "STATE",
            });
            const costList = costRes?.data?.list ?? [];
            const getDisplayName = (costMappingId) => {
              if (!costMappingId) return "";
              if (costType === "HOSPITAL" && hospitalList.length > 0) {
                const h = hospitalList.find(
                  (x) =>
                    String(x?.id ?? "") === String(costMappingId) ||
                    String(x?.hospitalId ?? "") === String(costMappingId) ||
                    String(x?.code ?? "") === String(costMappingId)
                );
                return h ? (h.name ?? h.hospitalName ?? costMappingId) : costMappingId;
              }
              if (costType === "NATIONAL" || costType === "GLOBAL") return "India";
              return costMappingId; // STATE: state name as is
            };
            costList.forEach((item) => {
              const procId = item?.proceduresschememaster?.proceduremaster?.procedureId ?? item?.procedureId ?? "";
              const cid = item?.costMappingId ?? item?.hospitalId ?? "";
              if (!procId || !cid) return;
              const key = `${schemeId}|${procId}`;
              if (!costMap[key]) costMap[key] = [];
              const name = getDisplayName(cid);
              if (name && !costMap[key].includes(name)) costMap[key].push(name);
            });
          } catch {
            // ignore
          }
        })
      );

      // Enrich and group by (schemeId, procedureId) - one row per unique scheme+procedure
      const grouped = new Map();
      rawMapped.forEach((m) => {
        const key = `${m.schemeId}|${m.procedureId}`;
        const stateHospitalNames = (costMap[key] || []).join(", ") || "—";
        if (!grouped.has(key)) {
          const sch = schemes.find((s) => String(s.id) === String(m.schemeId));
          const cc = sch?.schemeCountryId?.trim();
          const countryLabel = cc ? Country.getCountryByCode(cc)?.name ?? cc : "—";
          const schemeStateCodesDisplay = formatStateIdsForTable(sch?.stateIds);
          grouped.set(key, {
            ...m,
            stateHospitalDisplay: stateHospitalNames,
            schemeCountryLabel: countryLabel,
            schemeStateCodesDisplay,
          });
        }
      });

      const rawGroupedList = Array.from(grouped.values());
      setBaseGroupedMappings(rawGroupedList);
    } catch (err) {
      handleApiErrorResponses(err);
      setBaseGroupedMappings([]);
      setAllGroupedMappings([]);
      setMappings([]);
      setTotalCount(0);
    } finally {
      setMappingsLoading(false);
    }
  };

  const fetchHospitalList = async () => {
    try {
      const res = await HospitalServices.gethospitals("", 1, 1500);
      setHospitalList(res?.data?.list ?? []);
    } catch (err) {
      setHospitalList([]);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchSchemes(),
          getUsersDetails(),
          fetchHospitalList(),
        ]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setProcedureSearchKey(procedureSearchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [procedureSearchInput]);

  useEffect(() => {
    if (schemes.length === 0) return;
    setPage(1); // reset to first page when filters change
    fetchMappings();
  }, [schemeFilter, schemes.length, hospitalList.length]);

  /** Procedure name / ID search: client-side so full long names match reliably */
  useEffect(() => {
    const searchQ = procedureSearchKey.trim();
    const list = searchQ
      ? baseGroupedMappings.filter((m) => {
          const needle = normalizeProcedureNameSearch(searchQ);
          const name = normalizeProcedureNameSearch(m.procedureName ?? "");
          const pid = String(m.procedureId ?? "").toLowerCase();
          return (
            name.includes(needle) ||
            pid.includes(searchQ.toLowerCase().trim())
          );
        })
      : baseGroupedMappings;
    setAllGroupedMappings(list);
    setTotalCount(list.length);
    setTotalPages(Math.max(1, Math.ceil(list.length / PAGE_SIZE)));
  }, [baseGroupedMappings, procedureSearchKey]);

  // Client-side pagination: slice grouped data for current page
  useEffect(() => {
    const start = (page - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    setMappings(allGroupedMappings.slice(start, end));
  }, [page, allGroupedMappings]);

  const handleSave = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    const schemeId = currentMapping?.schemeId;
    if (!schemeId) {
      toast.error("Please select a Scheme.");
      savingRef.current = false;
      setSaving(false);
      return;
    }

    const scheme = schemes.find((s) => String(s.id) === String(schemeId));
    const procedureCostMapping = String(scheme?.procedureCostMapping ?? "").toUpperCase().trim();
    const isState = procedureCostMapping === "STATE";
    const isHospital = procedureCostMapping === "HOSPITAL";
    const isNationalOrGlobal = ["NATIONAL", "GLOBAL"].includes(procedureCostMapping);

    const buildMapProcedureSchemeCostPayload = (procedureIds) => {
      const items = [];
      if (isState && selectedStates.length > 0) {
        procedureIds.forEach((procedureId) => {
          selectedStates.forEach((stateName) => {
            items.push({
              schemeId: String(schemeId),
              procedureId: String(procedureId),
              procedureCostMapping: "STATE",
              costMappingId: stateName,
            });
          });
        });
      } else if (isHospital && selectedHospitals.length > 0) {
        procedureIds.forEach((procedureId) => {
          selectedHospitals.forEach((h) => {
            const costMappingId = h.id ?? h.hospitalId ?? h.name ?? "";
            if (costMappingId) {
              items.push({
                schemeId: String(schemeId),
                procedureId: String(procedureId),
                procedureCostMapping: "HOSPITAL",
                costMappingId: String(costMappingId),
              });
            }
          });
        });
      } else if (isNationalOrGlobal) {
        const countryId = selectedCountry?.trim() || "IN";
        procedureIds.forEach((procedureId) => {
          items.push({
            schemeId: String(schemeId),
            procedureId: String(procedureId),
            procedureCostMapping,
            costMappingId: countryId,
          });
        });
      }
      return items;
    };

    if (panelType === "add") {
      if (selectedProcedures.length === 0 && selectedForUnmap.length === 0) {
        toast.error("Please select at least one Procedure to map or unmap.");
        savingRef.current = false;
        setSaving(false);
        return;
      }
      if (selectedProcedures.length > 0) {
        if (isState && selectedStates.length === 0) {
          toast.error("This scheme has no states configured. Cannot map procedure cost.");
          savingRef.current = false;
          setSaving(false);
          return;
        }
        if (isHospital && selectedHospitals.length === 0) {
          toast.error("Please select at least one Hospital for this scheme.");
          savingRef.current = false;
          setSaving(false);
          return;
        }
      }
      try {
        const promises = [];
        if (selectedProcedures.length > 0) {
          const mapPayload = selectedProcedures.map((procedureId) => ({
            schemeId: String(schemeId),
            procedureId: String(procedureId),
          }));
          promises.push(ProcedureFlowServices.updateProcedureSchemeMapping(mapPayload));
          const costPayload = buildMapProcedureSchemeCostPayload(selectedProcedures);
          if (costPayload.length > 0) {
            promises.push(ProcedureFlowServices.mapProcedureSchemeCost(costPayload));
          }
        }
        if (selectedForUnmap.length > 0) {
          const unmapPayload = selectedForUnmap.map((procedureId) => ({
            schemeId: String(schemeId),
            procedureId: String(procedureId),
            status: "INACTIVE",
          }));
          promises.push(ProcedureFlowServices.updateProcedureSchemeMapping(unmapPayload));
        }
        await Promise.all(promises);
        const msgs = [];
        if (selectedProcedures.length > 0)
          msgs.push(`${selectedProcedures.length} mapped`);
        if (selectedForUnmap.length > 0)
          msgs.push(`${selectedForUnmap.length} unmapped`);
        toast.success(`Scheme - Procedure Mapping updated (${msgs.join(", ")}).`);
        setPanelVisible(false);
        setCurrentMapping({ schemeId: "", schemeName: "", procedureId: "", procedureName: "" });
        setInitialMappingForEdit(null);
        setSelectedProcedures([]);
        setSelectedForUnmap([]);
        setSelectedStates([]);
        setSelectedHospitals([]);
        await fetchMappings();
      } catch (err) {
        handleApiErrorResponses(err);
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
      return;
    }

    // Edit mode: single procedure
    const procedureId = currentMapping?.procedureId;
    if (!procedureId) {
      toast.error("Please select a Procedure.");
      savingRef.current = false;
      setSaving(false);
      return;
    }
    try {
      await ProcedureFlowServices.updateProcedureSchemeMapping([
        { schemeId: String(schemeId), procedureId: String(procedureId) },
      ]);
      const costPayload = buildMapProcedureSchemeCostPayload([procedureId]);
      if (costPayload.length > 0) {
        await ProcedureFlowServices.mapProcedureSchemeCost(costPayload);
      }
      toast.success("Scheme - Procedure Mapping updated successfully.");
      setPanelVisible(false);
      setCurrentMapping({ schemeId: "", schemeName: "", procedureId: "", procedureName: "" });
      setInitialMappingForEdit(null);
      setSelectedStates([]);
      setSelectedHospitals([]);
      await fetchMappings();
    } catch (err) {
      handleApiErrorResponses(err);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const openAdd = () => {
    setPanelVisible(true);
    setPanelType("add");
    setCurrentMapping({
      schemeId: "",
      schemeName: "",
      procedureId: "",
      procedureName: "",
    });
    setSelectedState("");
    setSelectedHospital(null);
    setSelectedStates([]);
    setSelectedHospitals([]);
    setSelectedCountry("IN");
    setHospitalDropdownValue("");
    setInitialMappingForEdit(null);
    setSelectedProcedures([]);
    setSelectedForUnmap([]);
    setAlreadyMappedProcedureIds(new Set());
    fetchAllProceduresFromMaster();
  };

  const openEdit = async (mapping) => {
    setPanelVisible(true);
    setPanelType("edit");
    const scheme = schemes.find((s) => String(s.id) === String(mapping.schemeId ?? ""));
    const costType = String(scheme?.procedureCostMapping ?? "").toUpperCase().trim();
    const stateIds = scheme?.stateIds ?? [];
    const prefillStates =
      costType === "STATE" ? getActiveStateNamesFromSchemeStateIds(stateIds) : [];
    const mappingData = {
      id: mapping.id,
      schemeId: mapping.schemeId,
      schemeName: mapping.schemeName,
      procedureId: mapping.procedureId,
      procedureName: mapping.procedureName,
      status: mapping.status,
      mappedBy: mapping.mappedBy,
      mappedOn: mapping.mappedOn,
    };
    setCurrentMapping(mappingData);
    setInitialMappingForEdit(mappingData);
    setSelectedProcedures([]);
    setSelectedStates(prefillStates);
    setHospitalDropdownValue("");

    // When HOSPITAL type, fetch cost list to get hospital(s) for this scheme+procedure
    if (costType === "HOSPITAL" && mapping.schemeId && mapping.procedureId) {
      try {
        const res = await ProcedureFlowServices.getSchemeProcedureCostList({
          schemeId: mapping.schemeId,
          procedureCostMapping: "HOSPITAL",
        });
        const list = res?.data?.list ?? [];
        const procedureIdStr = String(mapping.procedureId ?? "");
        const costItemsForProcedure = list.filter((item) => {
          const procId = item?.proceduresschememaster?.proceduremaster?.procedureId ?? item?.procedureId ?? "";
          return String(procId) === procedureIdStr;
        });
        const hospitalIds = costItemsForProcedure
          .map((item) => item?.costMappingId ?? item?.hospitalId ?? "")
          .filter(Boolean);
        if (hospitalIds.length > 0 && hospitalList.length > 0) {
          const hospitals = hospitalIds
            .map((hid) =>
              hospitalList.find(
                (h) =>
                  String(h?.id ?? "") === String(hid) ||
                  String(h?.hospitalId ?? "") === String(hid) ||
                  String(h?.code ?? "") === String(hid)
              )
            )
            .filter(Boolean);
          setSelectedHospitals(hospitals);
        } else {
          setSelectedHospitals([]);
        }
      } catch {
        setSelectedHospitals([]);
      }
    } else {
      setSelectedHospitals([]);
    }

    fetchAllProceduresFromMaster();
    fetchAlreadyMappedProcedures(mapping.schemeId);
  };

  const openView = (mapping) => {
    setPanelVisible(true);
    setPanelType("view");
    const sch = schemes.find((s) => String(s.id) === String(mapping.schemeId));
    const stateIdsArr = sch?.stateIds ?? [];
    const stateNames = getActiveStateNamesFromSchemeStateIds(stateIdsArr);
    const cc = sch?.schemeCountryId?.trim();
    const schemeCountryLabel = cc ? Country.getCountryByCode(cc)?.name ?? cc : "—";
    setCurrentMapping({
      id: mapping.id,
      schemeId: mapping.schemeId,
      schemeName: mapping.schemeName,
      procedureId: mapping.procedureId,
      procedureName: mapping.procedureName,
      status: mapping.status,
      mappedBy: mapping.mappedBy,
      mappedOn: mapping.mappedOn,
      schemeCountryLabel,
      schemeStateNamesLine: stateNames.length ? stateNames.join(", ") : "—",
      schemeStateCount: stateNames.length,
    });
  };

  const handleToggleClick = async (mapping) => {
    if (mapping.status === "ACTIVE") {
      setRowForInactive(mapping);
      setInactiveModalOpen(true);
      return;
    }
    try {
      await ProcedureFlowServices.updateProcedureSchemeMapping([
        {
          schemeId: String(mapping.schemeId),
          procedureId: String(mapping.procedureId),
          status: "ACTIVE",
        },
      ]);
      toast.success("Mapping activated.");
      await fetchMappings();
    } catch (err) {
      handleApiErrorResponses(err);
    }
  };

  const handleConfirmInactive = async () => {
    if (!rowForInactive) {
      setInactiveModalOpen(false);
      return;
    }
    try {
      await ProcedureFlowServices.updateProcedureSchemeMapping([
        {
          schemeId: String(rowForInactive.schemeId),
          procedureId: String(rowForInactive.procedureId),
          status: "INACTIVE",
        },
      ]);
      toast.success("Scheme - Procedure Mapping deleted.");
      await fetchMappings();
    } catch (err) {
      handleApiErrorResponses(err);
    } finally {
      setRowForInactive(null);
      setInactiveModalOpen(false);
    }
  };

  /** Open Edit Scheme modal (same as Manage Scheme) to add/update states on the scheme */
  const openEditSchemeModalForStates = () => {
    const scheme = schemes.find((s) => String(s.id) === String(currentMapping?.schemeId ?? ""));
    if (!scheme) {
      toast.warn("Select a scheme first.");
      return;
    }
    setSchemeForEditModal(scheme);
    setEditSchemeModalOpen(true);
  };

  const handleEditSchemeModalSaved = async () => {
    const sid = schemeForEditModal?.id;
    const list = await fetchSchemes();
    if (!sid) return;
    const sidStr = String(sid);
    const updated = list?.find((s) => String(s.id) === sidStr);
    if (!updated) return;
    if (String(currentMapping?.schemeId) === sidStr) {
      const costType = String(updated?.procedureCostMapping ?? "").toUpperCase().trim();
      const stateIds = updated?.stateIds ?? [];
      const prefillStates =
        costType === "STATE" ? getActiveStateNamesFromSchemeStateIds(stateIds) : [];
      setSelectedStates(prefillStates);
      setCurrentMapping((prev) => ({
        ...prev,
        schemeName: updated.name ?? prev.schemeName,
      }));
    }
  };

  return (
    <div className="w-full min-h-screen min-h-[100dvh] flex flex-col">
      <div className="bg-[radial-gradient(81.98%_338.81%_at_13.41%_276.08%,_rgba(90,198,159,0.5)_0%,_rgba(222,222,222,0.5)_100%)] flex-1 min-h-full w-full pt-6 pb-3 px-3 sm:pt-8 sm:pb-4 sm:px-4 md:pt-12 md:pb-6 md:px-6">
        {/* HEADER: Title + Add Button */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-4 lg:gap-6 py-4 sm:py-6 md:py-8 mb-4 sm:mb-6">
          <h1 className="font-lufga font-normal text-[20px] sm:text-[22px] md:text-[24px] lg:text-[28px] xl:text-[32px] 2xl:text-[50px] leading-[1.15] tracking-[-0.07em] text-black flex-1 min-w-0 pr-2 sm:pr-4">
            Manage Scheme - Procedure Mapping ({totalCount})
          </h1>

          <button
            onClick={openAdd}
            className="font-lufga w-full sm:w-auto min-h-[52px] sm:min-h-[56px] lg:min-h-[60px] py-3 sm:py-3.5 lg:py-4 rounded-[54px] gap-1.5 sm:gap-2 px-3 sm:px-4 lg:px-6 border-2 border-solid border-[#5AC69F] bg-[#5AC69FCC] flex items-center justify-center font-medium text-black hover:opacity-90 transition-opacity shrink-0 text-sm sm:text-base text-center"
          >
            <span className="text-lg sm:text-xl leading-none shrink-0">+</span>
            <span className="leading-tight">
              Add Scheme Procedure Mapping
            </span>
          </button>
        </div>

        {/* FILTERS: Scheme dropdown + Procedure Name search */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6">
          <div className="relative w-full sm:w-[260px]">
            <select
              value={schemeFilter}
              onChange={(e) => {
                setSchemeFilter(e.target.value);
                setPage(1);
              }}
              className="w-full h-12 sm:h-14 px-4 sm:px-6 pr-12 border border-solid border-[#E0E0E0] rounded-[40px] bg-white font-lufga text-sm sm:text-base outline-none appearance-none cursor-pointer"
            >
              <option value="">Scheme</option>
              {schemes.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <img
              src={downIcon}
              alt="down"
              className="absolute right-5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
            />
          </div>

          <div className="relative flex-1 min-w-0">
            <img
              src={searchIcon}
              alt="search"
              className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 w-5 h-5 sm:w-6 sm:h-6 pointer-events-none"
            />
            <input
              type="text"
              placeholder="Procedure Name"
              value={procedureSearchInput}
              onChange={(e) => setProcedureSearchInput(e.target.value)}
              className="w-full h-12 sm:h-14 pl-11 sm:pl-14 pr-5 sm:pr-6 border border-solid border-[#E0E0E0] rounded-[54px] outline-none font-lufga text-sm sm:text-base text-[#1B1B1B] placeholder:text-gray-400 bg-white"
            />
          </div>
        </div>

        {/* TABLE */}
        <div
          className={`grid gap-6 transition-all duration-300 ${
            panelVisible ? "lg:grid-cols-[1fr_minmax(40vw,45vw)]" : "grid-cols-1"
          }`}
        >
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-sm overflow-x-auto p-4 sm:p-6 min-w-0">
            <div className="grid grid-cols-[56px_minmax(120px,200px)_minmax(140px,1fr)_minmax(88px,110px)_minmax(140px,220px)_120px] gap-3 min-w-[900px] px-0 sm:px-2 py-3 font-lufga font-semibold text-sm sm:text-base text-[#1B1B1B] border-b border-[#E0E0E0]">
              <span>SNo.</span>
              <span>Scheme Name</span>
              <span>Procedure Name</span>
              <span>Country</span>
              <span>State</span>
              <span className="text-right pr-2">Actions</span>
            </div>

            <div className="font-lufga divide-y divide-[#E8E8E8]">
              {loading || mappingsLoading ? (
                <div className="py-12 text-center text-gray-500">
                  Loading...
                </div>
              ) : mappings.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  No procedure - scheme mappings found.
                </div>
              ) : (
                mappings.map((mapping, index) => (
                  <MappingRow
                    key={mapping.id}
                    serialNo={(page - 1) * PAGE_SIZE + index + 1}
                    mapping={mapping}
                    onEdit={() => openEdit(mapping)}
                    onView={() => openView(mapping)}
                    onToggle={() => handleToggleClick(mapping)}
                    alternateBg={index % 2 === 1}
                  />
                ))
              )}
            </div>

            {!loading && !mappingsLoading && mappings.length > 0 && (
              <div className="flex justify-center mt-6 mb-4 sm:mb-6">
                <CustomPagination
                  page={page}
                  setPage={setPage}
                  totalPages={totalPages}
                />
              </div>
            )}
          </div>

          {/* RIGHT PANEL: Add / Edit / View */}
          {panelVisible && (
            <div className="bg-white rounded-2xl sm:rounded-3xl border border-[#E0E0E0] p-6 sm:p-8 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-lufga text-[32px] font-normal">
                  Scheme - Procedure Mapping
                </h2>
                <img
                  src={CLOSE}
                  alt="close"
                  onClick={() => setPanelVisible(false)}
                  className="w-6 h-6 cursor-pointer"
                />
              </div>

              {(panelType === "add" || panelType === "edit") && (
                <>
                  <div className="relative mb-6 w-full">
                    <select
                      value={currentMapping?.schemeId ?? ""}
                      onChange={(e) => {
                        const id = e.target.value;
                        const scheme = schemes.find((s) => String(s.id) === id);
                        const costType = String(scheme?.procedureCostMapping ?? "").toUpperCase().trim();
                        const stateIds = scheme?.stateIds ?? [];
                        const prefillStates =
                          costType === "STATE" ? getActiveStateNamesFromSchemeStateIds(stateIds) : [];
                        setCurrentMapping({
                          ...currentMapping,
                          schemeId: id,
                          schemeName: scheme?.name ?? "",
                        });
                        setSelectedState("");
                        setSelectedHospital(null);
                        setSelectedStates(prefillStates);
                        setSelectedHospitals([]);
                        setSelectedCountry("IN");
                        setHospitalDropdownValue("");
                        if (panelType === "add") {
                          setSelectedProcedures([]);
                          fetchAlreadyMappedProcedures(id);
                        }
                      }}
                      className="w-full h-[70px] px-6 pr-36 border-2 border-solid border-[#E0E0E0] rounded-[40px] outline-none font-lufga text-[16px] bg-white appearance-none cursor-pointer"
                    >
                      <option value="">Scheme Name</option>
                      {schemes.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                    <img
                      src={downIcon}
                      alt="down"
                      className="absolute right-6 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
                    />
                    {(currentMapping?.schemeId ?? "") && (
                      <span className="absolute right-12 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
                        Scheme Name
                      </span>
                    )}
                  </div>

                  {/* Dynamic fields based on scheme procedureCostMapping - show only when scheme is selected */}
                  {currentMapping?.schemeId && (() => {
                    const scheme = schemes.find((s) => String(s.id) === String(currentMapping?.schemeId ?? ""));
                    const costType = String(scheme?.procedureCostMapping ?? "").toUpperCase().trim();
                    const showCountry = costType === "NATIONAL" || costType === "GLOBAL";
                    const showState = costType === "STATE";
                    const showHospital = costType === "HOSPITAL";

                    return (
                      <div className="flex flex-col gap-4 mb-6">
                        {showCountry && (
                          <div className="relative w-full">
                            <select
                              value={selectedCountry}
                              onChange={(e) => setSelectedCountry(e.target.value)}
                              className="w-full h-[70px] px-6 pr-36 border-2 border-solid border-[#E0E0E0] rounded-[40px] outline-none font-lufga text-[16px] bg-white appearance-none cursor-pointer"
                            >
                              <option value="IN">India</option>
                            </select>
                            <img src={downIcon} alt="down" className="absolute right-6 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" />
                            <span className="absolute right-12 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">Country</span>
                          </div>
                        )}
                        {showState && (
                          <div className="w-full">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                              <span className="font-lufga text-[13px] text-gray-500">
                                States (as per scheme)
                              </span>
                              <button
                                type="button"
                                onClick={openEditSchemeModalForStates}
                                className="font-lufga text-sm text-[#1B7B6C] hover:underline underline-offset-2 shrink-0 text-left sm:text-right"
                              >
                                Add state
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2 min-h-[52px] px-4 py-3 rounded-[40px] border-2 border-solid border-[#E0E0E0] bg-[#F5F5F5]">
                              {selectedStates.length === 0 ? (
                                <span className="font-lufga text-sm text-gray-400 self-center">
                                  No states configured for this scheme.
                                </span>
                              ) : (
                                selectedStates.map((state) => (
                                  <span
                                    key={state}
                                    className="inline-flex items-center px-3 py-1.5 rounded-full bg-[#5AC69F]/25 text-[#1B1B1B] font-lufga text-xs font-medium border border-[#5AC69F] max-w-[220px]"
                                    title={String(state)}
                                  >
                                    <span className="min-w-0 truncate">
                                      {formatStateLabelForDisplay(state)}
                                    </span>
                                  </span>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                        {showHospital && (
                          <div className="w-full">
                            <FilterDropdown
                              placeholder={
                                selectedHospitals.length > 0
                                  ? `${selectedHospitals.length} hospital${selectedHospitals.length === 1 ? "" : "s"} selected`
                                  : "Select Hospital"
                              }
                              options={hospitalList.map((h) => h.name ?? h.hospitalName ?? "").filter(Boolean)}
                              value={hospitalDropdownValue}
                              setValue={(name) => {
                                const hosp = hospitalList.find((h) => (h.name ?? h.hospitalName ?? "") === name);
                                if (hosp && !selectedHospitals.some((x) => (x.id ?? x.hospitalId ?? x.name) === (hosp.id ?? hosp.hospitalId ?? hosp.name))) {
                                  setSelectedHospitals((prev) => [...prev, hosp]);
                                }
                                setHospitalDropdownValue("");
                              }}
                              enableSearch={true}
                              fullWidth={true}
                              clearValue=""
                              variant="panel"
                              keepOpenOnSelect={true}
                              selectedItems={selectedHospitals.map((h) => h.name ?? h.hospitalName ?? "")}
                            />
                            {selectedHospitals.length > 0 && (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {selectedHospitals.map((h) => {
                                  const name = h.name ?? h.hospitalName ?? "";
                                  const id = h.id ?? h.hospitalId ?? name;
                                  return (
                                    <span
                                      key={id}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#5AC69F]/25 text-[#1B1B1B] font-lufga text-xs font-medium border border-[#5AC69F] max-w-[200px]"
                                      title={name}
                                    >
                                      <span className="min-w-0 truncate">{name}</span>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedHospitals((prev) =>
                                            prev.filter((x) => (x.id ?? x.hospitalId ?? x.name) !== (h.id ?? h.hospitalId ?? h.name))
                                          );
                                        }}
                                        className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-[#5AC69F]/80 text-[#1B1B1B] text-[10px] font-bold cursor-pointer bg-transparent border-none leading-none shrink-0"
                                      >
                                        &#x2715;
                                      </button>
                                    </span>
                                  );
                                })}
                                <button
                                  type="button"
                                  onClick={() => setSelectedHospitals([])}
                                  className="font-lufga text-[11px] text-red-400 hover:text-red-600 underline"
                                >
                                  Clear All
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {panelType === "add" ? (
                    <div className="w-full mb-10">
                    <ProcedureMultiSelect
                      procedures={allProceduresFromMaster}
                      alreadyMappedIds={alreadyMappedProcedureIds}
                      selectedIds={selectedProcedures}
                      onToggle={(procedureId) => {
                        setSelectedProcedures((prev) => {
                          const id = String(procedureId);
                          if (prev.includes(id)) return prev.filter((x) => x !== id);
                          return [...prev, id];
                        });
                      }}
                      onSelectAll={() => {
                        const selectable = allProceduresFromMaster.filter(
                          (p) => !alreadyMappedProcedureIds.has(String(p.procedureId))
                        );
                        const ids = selectable.map((p) => String(p.procedureId));
                        setSelectedProcedures((prev) => {
                          const allSelected = ids.every((id) => prev.includes(id));
                          if (allSelected) return prev.filter((x) => !ids.includes(x));
                          const added = ids.filter((id) => !prev.includes(id));
                          return [...prev, ...added];
                        });
                      }}
                      loading={panelProceduresLoading}
                      unmapIds={selectedForUnmap}
                      onToggleUnmap={(procedureId) => {
                        setSelectedForUnmap((prev) => {
                          const id = String(procedureId);
                          if (prev.includes(id)) return prev.filter((x) => x !== id);
                          return [...prev, id];
                        });
                      }}
                    />
                    {(selectedProcedures.length > 0 || selectedForUnmap.length > 0) && (
                      <div className="mt-3 flex flex-col gap-4">
                        {selectedProcedures.length > 0 && (
                          <>
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="font-lufga text-[11px] font-semibold text-[#2E7D32]">
                                Request to Map ({selectedProcedures.length})
                              </span>
                              <button
                                type="button"
                                onClick={() => setSelectedProcedures([])}
                                className="font-lufga text-[11px] text-red-400 hover:text-red-600 transition-colors bg-transparent border-none cursor-pointer underline"
                              >
                                Clear All
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2 max-h-[100px] overflow-y-auto">
                              {selectedProcedures.map((id) => {
                                const proc = allProceduresFromMaster.find(
                                  (p) => String(p.procedureId) === id
                                );
                                return (
                                  <span
                                    key={id}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#5AC69F]/25 text-[#1B1B1B] font-lufga text-xs font-medium"
                                  >
                                    {proc ? proc.procedure : id}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setSelectedProcedures((prev) =>
                                          prev.filter((x) => x !== id)
                                        )
                                      }
                                      className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-[#5AC69F]/80 text-[#1B1B1B] text-[10px] font-bold cursor-pointer bg-transparent border-none leading-none"
                                    >
                                      ✕
                                    </button>
                                  </span>
                                );
                              })}
                            </div>
                          </>
                        )}

                        {selectedForUnmap.length > 0 && (
                          <>
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="font-lufga text-[11px] font-semibold text-[#FF4343]">
                                Request to Unmap ({selectedForUnmap.length})
                              </span>
                              <button
                                type="button"
                                onClick={() => setSelectedForUnmap([])}
                                className="font-lufga text-[11px] text-red-400 hover:text-red-600 transition-colors bg-transparent border-none cursor-pointer underline"
                              >
                                Clear All
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2 max-h-[100px] overflow-y-auto">
                              {selectedForUnmap.map((id) => {
                                const proc = allProceduresFromMaster.find(
                                  (p) => String(p.procedureId) === id
                                );
                                return (
                                  <span
                                    key={id}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-lufga text-xs font-medium border border-[#FF4343]/60 text-[#1B1B1B] bg-[#FF4343]/40"
                                  >
                                    {proc ? proc.procedure : id}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setSelectedForUnmap((prev) =>
                                          prev.filter((x) => x !== id)
                                        )
                                      }
                                      className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-[#FF4343]/60 text-[#1B1B1B] text-[10px] font-bold cursor-pointer bg-transparent border-none leading-none"
                                    >
                                      ✕
                                    </button>
                                  </span>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    </div>
                  ) : (
                    <div className="w-full mb-10">
                      <FilterDropdown
                        fullWidth
                        placeholder={
                          panelProceduresLoading
                            ? "Loading procedures..."
                            : "Procedure Name"
                        }
                        options={allProceduresFromMaster
                          .filter(
                            (p) =>
                              !alreadyMappedProcedureIds.has(String(p.procedureId)) ||
                              String(p.procedureId) === String(currentMapping?.procedureId)
                          )
                          .map((p) => p.procedureId)}
                        value={currentMapping?.procedureId ?? ""}
                        setValue={(procedureId) => {
                          const proc = allProceduresFromMaster.find(
                            (p) => String(p.procedureId) === String(procedureId)
                          );
                          setCurrentMapping({
                            ...currentMapping,
                            procedureId: procedureId || "",
                            procedureName: proc?.procedure ?? "",
                          });
                        }}
                        clearValue=""
                        labelMap={Object.fromEntries(
                          allProceduresFromMaster.map((p) => [
                            p.procedureId,
                            `${p.procedure} (${p.procedureId})`,
                          ])
                        )}
                        enableSearch={true}
                        disabled={panelProceduresLoading}
                      />
                    </div>
                  )}

                  {(() => {
                    const hasChanges =
                      panelType === "add"
                        ? selectedProcedures.length > 0 || selectedForUnmap.length > 0
                        : panelType !== "edit" ||
                          !initialMappingForEdit ||
                          String(currentMapping?.schemeId ?? "") !== String(initialMappingForEdit?.schemeId ?? "") ||
                          String(currentMapping?.procedureId ?? "") !== String(initialMappingForEdit?.procedureId ?? "") ||
                          selectedStates.length > 0 ||
                          selectedHospitals.length > 0;
                    return (
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving || !hasChanges}
                        className="w-full h-[70px] border-2 border-solid border-[#5AC69F] bg-emerald-400 rounded-[40px] font-lufga font-semibold text-[#000000] hover:bg-emerald-500 transition disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {saving ? "Saving..." : "Save"}
                      </button>
                    );
                  })()}
                </>
              )}

              {panelType === "view" && (
                <div className="font-lufga text-gray-700 space-y-3">
                  <div className="flex flex-row flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <strong className="shrink-0">Scheme Name :</strong>
                    <span className="text-[#1B1B1B] font-medium min-w-0 flex-1 break-words">
                      {currentMapping?.schemeName ?? "—"}
                    </span>
                  </div>
                  <div className="flex flex-row flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <strong className="shrink-0">Country :</strong>
                    <span className="text-[#1B1B1B] font-medium min-w-0 flex-1 break-words">
                      {currentMapping?.schemeCountryLabel ?? "—"}
                    </span>
                  </div>
                  <div className="flex flex-row flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <strong className="shrink-0">
                      States ({currentMapping?.schemeStateCount ?? 0}) :
                    </strong>
                    <span className="text-[#1B1B1B] font-medium min-w-0 flex-1 break-words">
                      {currentMapping?.schemeStateNamesLine ?? "—"}
                    </span>
                  </div>
                  <div className="flex flex-row flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <strong className="shrink-0">Procedure Name :</strong>
                    <span className="text-[#1B1B1B] font-medium min-w-0 flex-1 break-words">
                      {currentMapping?.procedureName ?? "—"}
                    </span>
                  </div>
                  <div className="flex flex-row flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <strong className="shrink-0">Procedure Mapped by :</strong>
                    <span className="text-[#1B1B1B] font-medium min-w-0 flex-1 break-words">
                      {currentMapping?.mappedBy != null && users?.[currentMapping.mappedBy]
                        ? users[currentMapping.mappedBy]
                        : currentMapping?.mappedBy ?? "—"}
                    </span>
                  </div>
                  <div className="flex flex-row flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <strong className="shrink-0">Procedure Mapped on :</strong>
                    <span className="text-[#1B1B1B] font-medium min-w-0 flex-1 break-words">
                      {currentMapping?.mappedOn
                        ? new Date(currentMapping.mappedOn).toLocaleString(
                            "en-IN",
                            {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            }
                          )
                        : "—"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Inactive confirmation modal */}
      {inactiveModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 backdrop-blur-sm px-4">
          <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-xl px-6 py-8 sm:px-10">
            <button
              onClick={() => {
                setInactiveModalOpen(false);
                setRowForInactive(null);
              }}
              className="absolute top-6 right-6 bg-transparent border-none p-0 cursor-pointer"
            >
              <img src={closeIcon} alt="close" className="w-5 h-5" />
            </button>
            <div className="mt-2 flex justify-center overflow-x-auto overflow-y-hidden">
              <p className="font-lufga font-medium text-gray-800 text-base sm:text-lg whitespace-nowrap">
                Do you want to inactive Scheme - Procedure Mapping ?
              </p>
            </div>
            <div className="flex justify-center mt-8">
              <button
                onClick={handleConfirmInactive}
                className="rounded-[40px] px-8 py-4 min-h-[56px] border-2 border-solid border-[#5AC69F] bg-[#5AC69F] hover:opacity-90 font-lufga font-semibold text-white transition-opacity"
              >
                Inactive
              </button>
            </div>
          </div>
        </div>
      )}

      {editSchemeModalOpen && schemeForEditModal && (
        <EditSchemeModal
          open={editSchemeModalOpen}
          scheme={schemeForEditModal}
          onClose={() => {
            setEditSchemeModalOpen(false);
            setSchemeForEditModal(null);
          }}
          onSaved={handleEditSchemeModalSaved}
        />
      )}
    </div>
  );
}

function ProcedureMultiSelect({
  procedures,
  alreadyMappedIds,
  selectedIds,
  onToggle,
  onSelectAll,
  loading,
  unmapIds = [],
  onToggleUnmap,
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const ref = useRef(null);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && searchInputRef.current) setTimeout(() => searchInputRef.current?.focus(), 100);
    if (!open) {
      setSearchQuery("");
      setHighlightedIndex(-1);
    }
  }, [open]);

  const needle = normalizeProcedureNameSearch(searchQuery);
  const filtered = needle
    ? procedures.filter((p) => {
        const name = normalizeProcedureNameSearch(p.procedure ?? "");
        const pid = String(p.procedureId ?? "").toLowerCase().trim();
        return (
          name.includes(needle) ||
          pid.includes(searchQuery.trim().toLowerCase())
        );
      })
    : procedures;

  const alreadyMappedCount = procedures.filter(
    (p) => alreadyMappedIds.has(String(p.procedureId))
  ).length;

  const sortedFiltered = [...filtered].sort((a, b) => {
    const aMapped = alreadyMappedIds.has(String(a.procedureId));
    const bMapped = alreadyMappedIds.has(String(b.procedureId));
    if (aMapped && !bMapped) return -1;
    if (!aMapped && bMapped) return 1;
    return 0;
  });

  const selectableProcedures = procedures.filter(
    (p) => !alreadyMappedIds.has(String(p.procedureId))
  );
  const allSelectableSelected =
    selectableProcedures.length > 0 &&
    selectableProcedures.every((p) => selectedIds.includes(String(p.procedureId)));

  const totalSelections = selectedIds.length + unmapIds.length;
  const displayLabel = loading
    ? "Loading procedures..."
    : totalSelections === 0
      ? "Procedure Name"
      : [
          selectedIds.length > 0 && `${selectedIds.length} to map`,
          unmapIds.length > 0 && `${unmapIds.length} to unmap`,
        ].filter(Boolean).join(", ");

  return (
    <div ref={ref} className="relative w-full min-w-0">
      <div
        onClick={() => !loading && setOpen(!open)}
        className={`flex items-center gap-3 px-6 h-[70px] border-2 border-solid border-[#E0E0E0] rounded-[40px] 
          ${loading ? "bg-gray-50 cursor-not-allowed" : "bg-white cursor-pointer"}`}
      >
        <span
          className={`min-w-0 flex-1 truncate ${totalSelections ? "text-gray-700" : "text-gray-400"}`}
        >
          {displayLabel}
        </span>
        {totalSelections > 0 && !loading && (
          <span className="font-lufga text-[13px] text-gray-400 pointer-events-none shrink-0">
            Procedure Name
          </span>
        )}
        <img
          src={downIcon}
          alt="down"
          className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </div>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="sticky top-0 bg-white border-b border-gray-200 z-10">
            <div className="px-4 py-3">
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                <img src={searchIcon} alt="search" className="w-4 h-4 flex-shrink-0 opacity-50" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setHighlightedIndex(-1);
                  }}
                  onKeyDown={(e) => {
                    if (sortedFiltered.length === 0) return;
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setHighlightedIndex((prev) =>
                        prev < sortedFiltered.length - 1 ? prev + 1 : 0
                      );
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setHighlightedIndex((prev) =>
                        prev > 0 ? prev - 1 : sortedFiltered.length - 1
                      );
                    } else if (e.key === "Enter" && highlightedIndex >= 0 && highlightedIndex < sortedFiltered.length) {
                      e.preventDefault();
                      const p = sortedFiltered[highlightedIndex];
                      const id = String(p.procedureId);
                      if (alreadyMappedIds.has(id)) {
                        if (onToggleUnmap) onToggleUnmap(id);
                      } else {
                        onToggle(id);
                      }
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Search..."
                  className="w-full border-none outline-none bg-transparent text-sm text-gray-700 placeholder-gray-400"
                />
              </div>
            </div>
            {(selectedIds.length > 0 || unmapIds.length > 0) && (
              <div className="px-4 pb-3 border-t border-gray-100 pt-2 flex flex-col gap-4">
                {selectedIds.length > 0 && (
                  <>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-lufga text-[11px] font-semibold text-[#2E7D32]">
                        Request to Map ({selectedIds.length})

                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 max-h-[80px] overflow-y-auto">
                      {selectedIds.map((id) => {
                        const proc = procedures.find(
                          (p) => String(p.procedureId) === id
                        );
                        return (
                          <span
                            key={id}
                            title={proc ? formatProcedureOptionLabel(proc) : id}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#5AC69F]/25 text-[#1B1B1B] font-lufga text-[11px] font-medium max-w-[min(100%,280px)]"
                          >
                            <span className="min-w-0 truncate">
                              {proc ? formatProcedureOptionLabel(proc) : id}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onToggle(id);
                              }}
                              className="flex items-center justify-center w-3.5 h-3.5 rounded-full hover:bg-[#5AC69F]/80 text-[#1B1B1B] text-[9px] font-bold cursor-pointer bg-transparent border-none leading-none"
                            >
                              ✕
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </>
                )}
                {unmapIds.length > 0 && (
                  <>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-lufga text-[11px] font-semibold text-[#FF4343]">
                        Request to Unmap ({unmapIds.length})
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 max-h-[80px] overflow-y-auto">
                      {unmapIds.map((id) => {
                        const proc = procedures.find(
                          (p) => String(p.procedureId) === id
                        );
                        return (
                          <span
                            key={id}
                            title={proc ? formatProcedureOptionLabel(proc) : id}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-lufga text-[11px] font-medium border border-[#FF4343]/60 text-[#1B1B1B] bg-[#FF4343]/40 max-w-[min(100%,280px)]"
                          >
                            <span className="min-w-0 truncate">
                              {proc ? formatProcedureOptionLabel(proc) : id}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onToggleUnmap) onToggleUnmap(id);
                              }}
                              className="flex items-center justify-center w-3.5 h-3.5 rounded-full hover:bg-[#FF4343]/60 text-[#1B1B1B] text-[9px] font-bold cursor-pointer bg-transparent border-none leading-none"
                            >
                              ✕
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <div ref={listRef} className="max-h-[280px] overflow-y-auto">
            {selectableProcedures.length > 0 && (
              <div
                onClick={() => onSelectAll()}
                className="px-6 py-3 cursor-pointer flex items-center justify-between font-lufga text-sm border-b border-gray-200 bg-gray-50 hover:bg-gray-100"
              >
                <span className="font-medium">Select All Unmapped</span>
                {allSelectableSelected && (
                  <span className="text-[#2E7D32] font-bold">✓</span>
                )}
              </div>
            )}
            {sortedFiltered.length === 0 ? (
              <div className="px-6 py-8 text-center text-gray-500 font-lufga text-sm">
                No results found
              </div>
            ) : (
              (() => {
                const mappedInList = sortedFiltered.filter((p) => alreadyMappedIds.has(String(p.procedureId)));
                const unmappedInList = sortedFiltered.filter((p) => !alreadyMappedIds.has(String(p.procedureId)));
                return (
                  <>
                    {mappedInList.length > 0 && (
                      <div className="px-6 py-2 font-lufga text-xs font-semibold text-blue-600 bg-blue-50 border-b border-blue-100 sticky top-0 z-[5]">
                        Already Mapped ({alreadyMappedCount})
                      </div>
                    )}
                    {mappedInList.map((p) => {
                      const id = String(p.procedureId);
                      const isSelectedForUnmap = unmapIds.includes(id);
                      const globalIdx = sortedFiltered.indexOf(p);
                      const isHighlighted = highlightedIndex === globalIdx;
                      return (
                        <div
                          key={id}
                          ref={isHighlighted ? (el) => {
                            if (el && listRef.current) {
                              el.scrollIntoView({ block: "nearest", behavior: "smooth" });
                            }
                          } : undefined}
                          onClick={() => onToggleUnmap && onToggleUnmap(id)}
                          onMouseEnter={() => setHighlightedIndex(globalIdx)}
                          className={`px-6 py-3 flex items-center justify-between font-lufga text-sm border-b border-blue-50 cursor-pointer
                            ${isSelectedForUnmap
                              ? "bg-blue-100 text-blue-800 ring-1 ring-inset ring-blue-300"
                              : isHighlighted
                                ? "bg-blue-50/60 ring-1 ring-inset ring-blue-300"
                                : "bg-blue-50/40 text-blue-700 hover:bg-blue-50/70"}`}
                        >
                          <span
                            className="min-w-0 truncate flex-1"
                            title={formatProcedureOptionLabel(p)}
                          >
                            <span className="font-semibold text-[#1B5E3D]">{p.procedureId}</span>
                            <span className="text-gray-500"> — </span>
                            <span>{p.procedure}</span>
                          </span>
                          <span className={`ml-2 shrink-0 flex items-center gap-1.5 ${isSelectedForUnmap ? "text-blue-700" : "text-blue-400"}`}>
                            <span className={`inline-flex items-center justify-center w-[18px] h-[18px] rounded text-[11px] font-bold leading-none ${isSelectedForUnmap ? "border-2 bg-blue-600 border-blue-600 text-white" : "border-[3px] border-blue-600 bg-white"}`}>
                              {isSelectedForUnmap ? "✓" : ""}
                            </span>
                            <span className="text-xs font-medium text-blue-500">Mapped</span>
                          </span>
                        </div>
                      );
                    })}
                    {unmappedInList.length > 0 && mappedInList.length > 0 && (
                      <div className="px-6 py-2 font-lufga text-xs font-semibold text-gray-500 bg-gray-50 border-b border-gray-200 sticky top-0 z-[5]">
                        Unmapped ({unmappedInList.length})
                      </div>
                    )}
                    {unmappedInList.map((p) => {
                      const id = String(p.procedureId);
                      const isSelected = selectedIds.includes(id);
                      const globalIdx = sortedFiltered.indexOf(p);
                      const isHighlighted = highlightedIndex === globalIdx;
                      return (
                        <div
                          key={id}
                          ref={isHighlighted ? (el) => {
                            if (el && listRef.current) {
                              el.scrollIntoView({ block: "nearest", behavior: "smooth" });
                            }
                          } : undefined}
                          onClick={() => onToggle(id)}
                          onMouseEnter={() => setHighlightedIndex(globalIdx)}
                          className={`px-6 py-3 flex items-center justify-between font-lufga text-sm border-b border-gray-100 last:border-b-0
                            ${isSelected
                              ? "bg-[#E8F5E9] text-[#2E7D32] cursor-pointer hover:bg-[#C8E6C9]"
                              : isHighlighted
                                ? "bg-[#F0F9F4] cursor-pointer ring-1 ring-inset ring-[#5AC69F]"
                                : "cursor-pointer hover:bg-[#F4FBF8]"}`}
                        >
                          <span
                            className="min-w-0 truncate flex-1"
                            title={formatProcedureOptionLabel(p)}
                          >
                            <span className="font-semibold text-[#1B5E3D]">{p.procedureId}</span>
                            <span className="text-gray-500"> — </span>
                            <span>{p.procedure}</span>
                          </span>
                          {isSelected && (
                            <span className="text-[#2E7D32] font-bold ml-2 shrink-0">✓</span>
                          )}
                        </div>
                      );
                    })}
                  </>
                );
              })()
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MappingRow({
  serialNo,
  mapping,
  onEdit,
  onView,
  onToggle,
  alternateBg,
}) {
  const isActive = mapping.status !== "INACTIVE";

  return (
    <div
      className={`grid grid-cols-[56px_minmax(120px,200px)_minmax(140px,1fr)_minmax(88px,110px)_minmax(140px,220px)_120px] gap-3 min-w-[900px] px-0 sm:px-2 py-4 sm:py-5 items-center font-lufga ${
        alternateBg ? "bg-[#E8E8E833]" : ""
      }`}
    >
      <span className="font-medium text-sm sm:text-base text-[#1B1B1B]">
        {serialNo}
      </span>
      <p className="text-sm sm:text-base text-[#1B1B1B] text-gray-800 leading-relaxed break-words min-w-0">
        {mapping.schemeName ?? "—"}
      </p>
      <p className="text-sm sm:text-base text-[#1B1B1B] text-gray-800 leading-relaxed break-words min-w-0">
        {mapping.procedureName ?? "—"}
      </p>
      <span
        className="text-sm sm:text-base text-[#1B1B1B] text-gray-800 truncate"
        title={mapping.schemeCountryLabel ?? ""}
      >
        {mapping.schemeCountryLabel ?? "—"}
      </span>
      <span
        className="text-sm sm:text-base text-[#1B1B1B] text-gray-800 break-words min-w-0 line-clamp-3"
        title={mapping.schemeStateCodesDisplay ?? ""}
      >
        {mapping.schemeStateCodesDisplay ?? "—"}
      </span>
      <div className="flex items-center gap-3 sm:gap-4 justify-end pr-2">
        <img
          src={eyeIcon}
          alt="view"
          className="w-5 h-5 cursor-pointer flex-shrink-0"
          onClick={onView}
        />
        <img
          src={editIcon}
          alt="edit"
          className="w-5 h-5 cursor-pointer flex-shrink-0"
          onClick={onEdit}
        />
        <Switch
          checked={isActive}
          onChange={onToggle}
          size="small"
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: "#5AC69F",
              "& + .MuiSwitch-track": { backgroundColor: "#5AC69F !important", opacity: 0.6 },
            },
            "& .MuiSwitch-track": {
              backgroundColor: "#9e9e9e !important",
            },
          }}
        />
      </div>
    </div>
  );
}
